/**
 * Key assumptions table for the selected location under BAU:
 * EMFAC 2021 | 2025 × Conservative | Current Policy | Accelerated.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchKeyAssumptionsTable,
  type KeyAssumptionCell,
  type ScenarioCostOptions,
} from '../../services/scenarioCostModel';
import type { PolicyScenario } from '../../services/policyImpactService';
import type { BuildingsBauScenario } from '../../services/buildingsBauPathways';

const currencyShort = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1e9) return (v < 0 ? '-$' : '$') + (abs / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return (v < 0 ? '-$' : '$') + (abs / 1e6).toFixed(2) + 'M';
  return '$' + Math.round(v).toLocaleString();
};

const BUILDINGS_BAU_LABELS: Record<BuildingsBauScenario, string> = {
  conservative: 'Conservative',
  current_policy: 'Current policy',
  accelerated: 'Accelerated (+25% local)',
};

export interface KeyAssumptionsTableProps {
  location: string;
  scenario: PolicyScenario;
  options?: ScenarioCostOptions;
  /** Only show when results exist (e.g. from main tool). */
  enabled?: boolean;
  /** When true, omit the outer heading (e.g. when embedded under "Key assumptions by scenario"). */
  embedded?: boolean;
}

export const KeyAssumptionsTable: React.FC<KeyAssumptionsTableProps> = ({
  location,
  scenario,
  options,
  enabled = true,
  embedded = false,
}) => {
  const [cells, setCells] = useState<KeyAssumptionCell[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!location || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchKeyAssumptionsTable(location, scenario, options);
      setCells(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load key assumptions.');
      setCells(null);
    } finally {
      setLoading(false);
    }
  }, [location, scenario, options, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  if (!enabled || !location) return null;

  const columns = cells
    ? [
        { emfac: '2021' as const, bau: 'conservative' as const },
        { emfac: '2021' as const, bau: 'current_policy' as const },
        { emfac: '2021' as const, bau: 'accelerated' as const },
        { emfac: '2025' as const, bau: 'conservative' as const },
        { emfac: '2025' as const, bau: 'current_policy' as const },
        { emfac: '2025' as const, bau: 'accelerated' as const },
      ]
    : [];

  const getCell = (emfac: '2021' | '2025', bau: BuildingsBauScenario): KeyAssumptionCell | undefined =>
    cells?.find((c) => c.emfacVersion === emfac && c.buildingsBauScenario === bau);

  return (
    <section
      style={embedded ? {} : { marginTop: 24, paddingTop: 24, borderTop: '1px solid var(--border-color)' }}
    >
      {!embedded && (
        <>
          <h3 style={{ fontSize: '1rem', marginBottom: 8, color: 'var(--text-primary)' }}>
            Key assumptions (BAU) — {location}
          </h3>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
            Upfront costs, EV adoption, building electrification, and RNG share under each EMFAC and buildings BAU scenario.
          </p>
        </>
      )}
      {loading && <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Loading…</p>}
      {error && <p style={{ fontSize: '0.875rem', color: '#f87171' }}>{error}</p>}
      {!loading && !error && cells && cells.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '8px 10px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                  }}
                >
                  Assumption
                </th>
                {columns.map(({ emfac, bau }) => (
                  <th
                    key={`${emfac}-${bau}`}
                    style={{
                      textAlign: 'right',
                      padding: '8px 10px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                    }}
                  >
                    EMFAC{emfac} · {BUILDINGS_BAU_LABELS[bau]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>Upfront cost (buildings)</td>
                {columns.map(({ emfac, bau }) => (
                  <td
                    key={`${emfac}-${bau}`}
                    style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--text-primary)' }}
                  >
                    {currencyShort(getCell(emfac, bau)?.buildingsUpfrontCost ?? 0)}
                  </td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>Upfront cost (transport)</td>
                {columns.map(({ emfac, bau }) => (
                  <td
                    key={`${emfac}-${bau}`}
                    style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--text-primary)' }}
                  >
                    {currencyShort(getCell(emfac, bau)?.transportUpfrontCost ?? 0)}
                  </td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>EV% (2023)</td>
                {columns.map(({ emfac, bau }) => (
                  <td
                    key={`${emfac}-${bau}`}
                    style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--text-primary)' }}
                  >
                    {(getCell(emfac, bau)?.evPercent2023 ?? 0).toFixed(1)}%
                  </td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>EV% (2045 BAU)</td>
                {columns.map(({ emfac, bau }) => (
                  <td
                    key={`${emfac}-${bau}`}
                    style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--text-primary)' }}
                  >
                    {(getCell(emfac, bau)?.evPercent2045 ?? 0).toFixed(1)}%
                  </td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>
                  Building electrified stock % (2045)
                </td>
                {columns.map(({ emfac, bau }) => (
                  <td
                    key={`${emfac}-${bau}`}
                    style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--text-primary)' }}
                  >
                    {((getCell(emfac, bau)?.buildingElectrifiedStock2045 ?? 0) * 100).toFixed(1)}%
                  </td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>RNG share % (2045)</td>
                {columns.map(({ emfac, bau }) => (
                  <td
                    key={`${emfac}-${bau}`}
                    style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--text-primary)' }}
                  >
                    {((getCell(emfac, bau)?.rngShare2045 ?? 0) * 100).toFixed(0)}%
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
